import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { rupees, can } from "@/lib/helpers";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";

const empty = {
  name: "", sku: "", category_id: "", metal: "Gold", purity: "22K",
  gross_weight: 0, net_weight: 0, stone_weight: 0, diamond_weight: 0, fixed_price: 0,
  making_type: "per_gram", making_value: 0, wastage_percent: 0, stone_charge: 0,
  hsn: "7113", gst_rate: 3,
};

export default function Products() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const canCreate = can(user, "products", "create");
  const canEdit = can(user, "products", "edit");

  const load = useCallback(async () => {
    const { data } = await api.get(`/products${search ? `?search=${search}` : ""}`);
    setRows(data);
  }, [search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };

  const num = (o) => {
    const n = { ...o };
    ["gross_weight", "net_weight", "stone_weight", "diamond_weight", "fixed_price", "making_value", "wastage_percent", "stone_charge", "gst_rate"].forEach((k) => n[k] = Number(n[k] || 0));
    return n;
  };

  const submit = async () => {
    try {
      if (!form.name.trim() || !form.sku.trim()) return toast.error("Name and SKU are required");
      if (editId) {
        const { id, created_at, category_name, barcode, ...payload } = num(form);
        await api.put(`/products/${editId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", num(form));
        toast.success("Product created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Product Master" subtitle="Jewellery catalogue with metal, purity, weights & charges.">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU / name…" className="pl-9 w-56 bg-white border-[#E6E2D6]" data-testid="product-search" />
        </div>
        {canCreate && <Button onClick={openNew} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-product-btn"><Plus size={16} className="mr-1" /> New Product</Button>}
      </PageHeader>

      <Card className="bg-white border-[#E6E2D6] shadow-sm">
        {rows.length === 0 ? <EmptyState /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
                  <TableHead>Metal</TableHead>
                  <TableHead>Purity</TableHead><TableHead className="text-right">Net Wt (g)</TableHead>
                  <TableHead>Making</TableHead><TableHead className="text-right">Stone ₹</TableHead>
                  <TableHead>HSN</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id} data-testid={`product-row-${p.sku}`}>
                    <TableCell className="font-medium">{p.sku}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell className="text-sm">{p.category_name || "-"}</TableCell>
                    <TableCell className="text-sm">{p.metal || "Gold"}</TableCell>
                    <TableCell>{["Diamond", "Stone"].includes(p.metal) ? rupees(p.fixed_price || 0).replace(".00", "") : p.purity}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(p.net_weight).toFixed(3)}</TableCell>
                    <TableCell className="text-xs">{p.making_type === "per_gram" ? `₹${p.making_value}/g` : p.making_type === "percent" ? `${p.making_value}%` : `₹${p.making_value}`}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupees(p.stone_charge).replace(".00", "")}</TableCell>
                    <TableCell className="text-sm">{p.hsn}</TableCell>
                    <TableCell>{canEdit && <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.sku}`}><PencilSimple size={16} /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Name <span className="text-[#9D2A2A]">*</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-name" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">SKU <span className="text-[#9D2A2A]">*</span></Label><Input value={form.sku} disabled={!!editId} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-sku" /></div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Category</Label>
              <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Metal / Type</Label>
              <Select value={form.metal} onValueChange={(v) => setForm({ ...form, metal: v, purity: v === "Gold" ? "22K" : v === "Silver" ? "Silver 925" : "NA" })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-metal"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["Gold", "Silver", "Diamond", "Stone"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {["Diamond", "Stone"].includes(form.metal) ? (
              <div><Label className="text-xs text-[#6B6B6B]">Fixed Price ₹ (per item)</Label><Input type="number" value={form.fixed_price} onChange={(e) => setForm({ ...form, fixed_price: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-fixed-price" /></div>
            ) : (
              <div>
                <Label className="text-xs text-[#6B6B6B]">Purity</Label>
                <Select value={form.purity} onValueChange={(v) => setForm({ ...form, purity: v })}>
                  <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-purity"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{(form.metal === "Silver" ? ["Silver 999", "Silver 925"] : ["24K", "22K", "18K"]).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {[["gross_weight", "Gross Wt (g)"], ["net_weight", "Net Wt (g)"], ["stone_weight", "Stone Wt (g)"], ["diamond_weight", "Diamond Wt (ct)"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs text-[#6B6B6B]">{l}</Label><Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid={`pf-${k}`} /></div>
            ))}
            <div>
              <Label className="text-xs text-[#6B6B6B]">Making Type</Label>
              <Select value={form.making_type} onValueChange={(v) => setForm({ ...form, making_type: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-making-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="per_gram">Per Gram (₹)</SelectItem>
                  <SelectItem value="percent">Percent (%)</SelectItem>
                  <SelectItem value="fixed">Fixed (₹)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#6B6B6B]">Making Value</Label><Input type="number" value={form.making_value} onChange={(e) => setForm({ ...form, making_value: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-making-value" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Wastage %</Label><Input type="number" value={form.wastage_percent} onChange={(e) => setForm({ ...form, wastage_percent: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-wastage" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Stone Charge ₹</Label><Input type="number" value={form.stone_charge} onChange={(e) => setForm({ ...form, stone_charge: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-stone-charge" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">HSN</Label><Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-hsn" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">GST %</Label><Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pf-gst" /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="pf-save">{editId ? "Save Changes" : "Create Product"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
